// src/pages/vendor/Dashboard.jsx - ENHANCED MODERN VERSION
import React, { useEffect, useState } from 'react';
import { useVendor } from '../../hooks/useVendor';
import {
  Box,
  LinearProgress,
  Paper,
  Typography,
  Avatar,
  Stack,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
  Divider,
  Tooltip,
  Badge,
  Skeleton,
} from '@mui/material';
import {
  Store,
  Star,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  AttachMoney,
  People,
  Inventory,
  ArrowForward,
  Refresh,
  Download,
  Notifications,
  CalendarToday,
  LocalShipping,
  Warning,
  Info,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useThemeContext } from '../../context/ThemeContext';
import StatsCards from '../../components/vendor/StatsCards';
import RecentOrders from '../../components/vendor/RecentOrders';
import TopProducts from '../../components/vendor/TopProducts';
import QuickActions from '../../components/vendor/QuickActions';
import RevenueChart from '../../components/vendor/RevenueChart';

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { colors } = useThemeContext();
  const { stats, loading, fetchStats, orders, products, fetchOrders, fetchProducts } = useVendor();
  const [revenueData, setRevenueData] = useState([]);
  const [notifications, setNotifications] = useState([
    { id: 1, message: 'New order #ORD-1234 received', time: '5 min ago', type: 'order' },
    { id: 2, message: 'Product "Premium Shoes" is low in stock', time: '1 hour ago', type: 'stock' },
    { id: 3, message: 'Payment of $450.00 processed', time: '3 hours ago', type: 'payment' },
  ]);

  const isDark = colors?.themeMode === 'dark';
  const primaryColor = colors?.primaryColor || '#E31E24';

  useEffect(() => {
    fetchStats();
    fetchOrders();
    fetchProducts();
    
    // Simulate revenue data - replace with real API call
    setRevenueData([
      { month: 'Jan', revenue: 1250, orders: 45 },
      { month: 'Feb', revenue: 1800, orders: 52 },
      { month: 'Mar', revenue: 2200, orders: 68 },
      { month: 'Apr', revenue: 1900, orders: 55 },
      { month: 'May', revenue: 3100, orders: 89 },
      { month: 'Jun', revenue: 2800, orders: 76 },
    ]);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order': return <ShoppingBag sx={{ color: 'primary.main' }} />;
      case 'stock': return <Warning sx={{ color: 'warning.main' }} />;
      case 'payment': return <AttachMoney sx={{ color: 'success.main' }} />;
      default: return <Info sx={{ color: 'info.main' }} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4, px: { xs: 2, sm: 3 } }}>
        <Skeleton variant="rounded" height={120} sx={{ mb: 3, borderRadius: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} sx={{ mt: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={200} sx={{ mt: 3, borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 }, px: { xs: 0, sm: 0 } }}>
      
      {/* Welcome Banner - Enhanced */}
      <Slide direction="down" in timeout={500}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: 3,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 50%, ${primaryColor}bb 100%)`,
            color: 'white',
            borderRadius: { xs: 2, sm: 3 },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative Background Circles */}
          <Box
            sx={{
              position: 'absolute',
              right: -40,
              top: -40,
              width: { xs: 150, sm: 200 },
              height: { xs: 150, sm: 200 },
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.08)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: -100,
              bottom: -100,
              width: { xs: 200, sm: 300 },
              height: { xs: 200, sm: 300 },
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.04)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              left: -80,
              top: -80,
              width: { xs: 120, sm: 180 },
              height: { xs: 120, sm: 180 },
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.03)',
            }}
          />

          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            spacing={{ xs: 2, sm: 3 }}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  width: { xs: 48, sm: 56, md: 64 },
                  height: { xs: 48, sm: 56, md: 64 },
                  border: '2px solid rgba(255,255,255,0.3)',
                }}
              >
                <Store sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
              </Avatar>
              <Box>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                >
                  {getGreeting()}, {user?.storeName || 'Vendor'}! 👋
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.9,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                >
                  Here's what's happening with your store today
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ flexGrow: 1 }} />

            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1} 
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <Tooltip title="Store Status">
                <Chip
                  icon={<CheckCircle />}
                  label="Store Active"
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              </Tooltip>
              <Tooltip title="Average Rating">
                <Chip
                  icon={<Star />}
                  label={`${stats?.averageRating || 0} ⭐`}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              </Tooltip>
              <Tooltip title="Total Orders">
                <Chip
                  icon={<ShoppingBag />}
                  label={`${stats?.totalOrders || 0} Orders`}
                  size="small"
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              </Tooltip>
            </Stack>
          </Stack>

          {/* Quick Stats Row */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 1, sm: 3 }}
            sx={{ 
              mt: { xs: 2, sm: 3 }, 
              pt: { xs: 2, sm: 2.5 }, 
              borderTop: '1px solid rgba(255,255,255,0.15)',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <AttachMoney sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Today's Revenue</Typography>
              <Typography variant="body1" fontWeight="bold">${stats?.todayRevenue?.toFixed(2) || '0.00'}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShoppingBag sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Today's Orders</Typography>
              <Typography variant="body1" fontWeight="bold">{stats?.todayOrders || 0}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <People sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>New Customers</Typography>
              <Typography variant="body1" fontWeight="bold">{stats?.newCustomers || 0}</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocalShipping sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>Pending Shipments</Typography>
              <Typography variant="body1" fontWeight="bold">{stats?.pendingShipments || 0}</Typography>
            </Stack>
          </Stack>
        </Paper>
      </Slide>

      {/* Stats Cards */}
      <Fade in timeout={800}>
        <Box>
          <StatsCards stats={stats} />
        </Box>
      </Fade>

      {/* Charts & Top Products */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Slide direction="up" in timeout={600}>
            <Box>
              <RevenueChart data={revenueData} />
            </Box>
          </Slide>
        </Grid>
        <Grid item xs={12} md={4}>
          <Slide direction="up" in timeout={800}>
            <Box>
              <TopProducts products={products?.slice(0, 5) || []} />
            </Box>
          </Slide>
        </Grid>
      </Grid>

      {/* Recent Activity & Quick Actions */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={8}>
          <Slide direction="up" in timeout={700}>
            <Box>
              <RecentOrders orders={orders || []} />
            </Box>
          </Slide>
        </Grid>
        <Grid item xs={12} md={4}>
          <Slide direction="up" in timeout={900}>
            <Box>
              <QuickActions />
            </Box>
          </Slide>
        </Grid>
      </Grid>

      {/* Notifications Bar - New */}
      <Fade in timeout={1000}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mt: 3,
            borderRadius: { xs: 2, sm: 3 },
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'background.paper',
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Badge badgeContent={notifications.length} color="error">
                <Notifications sx={{ color: primaryColor }} />
              </Badge>
              <Typography variant="subtitle1" fontWeight="bold">
                Recent Notifications
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({notifications.length} unread)
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
              <Button 
                size="small" 
                variant="outlined"
                startIcon={<Refresh />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Mark All Read
              </Button>
              <Button 
                size="small" 
                variant="contained"
                startIcon={<ArrowForward />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                View All
              </Button>
            </Stack>
          </Stack>
          
          <Divider sx={{ my: 2 }} />
          
          <Stack spacing={1.5}>
            {notifications.slice(0, 3).map((notif, index) => (
              <Box
                key={notif.id}
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: 2,
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'grey.100',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getNotificationIcon(notif.type)}
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="500">
                      {notif.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <CalendarToday sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                      {notif.time}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  label={notif.type}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.55rem',
                    bgcolor: notif.type === 'order' ? 'primary.light' :
                             notif.type === 'stock' ? 'warning.light' : 'success.light',
                    color: notif.type === 'order' ? 'primary.main' :
                           notif.type === 'stock' ? 'warning.main' : 'success.main',
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Fade>

      {/* Footer Stats - New */}
      <Fade in timeout={1100}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mt: 3,
            borderRadius: { xs: 2, sm: 3 },
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'background.paper',
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Store Views</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                  {stats?.storeViews?.toLocaleString() || '0'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5, color: 'success.main' }}>
                  {stats?.conversionRate || '0'}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Average Order Value</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5, color: 'primary.main' }}>
                  ${stats?.averageOrderValue?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Customer Satisfaction</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5, color: 'success.main' }}>
                  {stats?.satisfaction || '0'}%
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Fade>
    </Box>
  );
};

export default Dashboard;